/* eslint-disable sonarjs/cognitive-complexity */
import { useEffect, useState, useContext, useRef } from 'react'
import root from 'react-shadow/styled-components'

import { ButtonStyle } from 'constants'

import { createOrUpdateRecipe } from 'api'
import { Analytics, AnalyticsContext, EventName, BroadcastEventName } from 'analytics'
import { V2ConfigurationContext } from 'contexts/Configuration'
import { getIframeURL, getHostPageUrl, getPageUrl, isPreviewMode, isValidIntersectionEntry, validateGrocers } from 'helper'
import { postBroadcastMessage, buildBroadcastMessage } from 'messenger'

import Modal from 'Modal'

import SingleStyleButton from './SingleStyleButton'
import MultiStyleButton from './MultiStyleButton'

const getButtonStyle = style => {
  if (style === ButtonStyle.SINGLE) return ButtonStyle.SINGLE
  return ButtonStyle.MULTI
}

const getButtonComponent = buttonStyle => {
  if (buttonStyle === ButtonStyle.SINGLE) return SingleStyleButton
  return MultiStyleButton
}

const ShadowDOMContainer = ({ children }) => (
  <root.div>
    {children}
  </root.div>
)

const WidgetV2 = ({ container, href, children }) => {
  const [recipe, setRecipe] = useState(null)
  const [analytics, setAnalytics] = useState(null)
  const [modalVisible, setModalVisible] = useState(false)
  const rootRef = useRef(null)

  const {
    partner, disablePoweredByInPopup, utmSource, utmCampaign, utmMedium, buttonStyle,
    highlightAiAdapter, lang, zipCode, externalId, tbrecipeId, servingSize, theme, grocers,
    enableAvailabilityAware, shoppablelinkId
  } = useContext(V2ConfigurationContext)

  validateGrocers(grocers, null)

  const recipeUrl = href || getPageUrl()

  const validatedStyle = getButtonStyle(buttonStyle)

  let impressionCount = 0
  let widgetViewedCount = 0
  let ButtonComponent = getButtonComponent(validatedStyle)

  // Send Widget API, send WidgetViewed event and setup click event handler
  useEffect(() => {
    const eventPayload = { count: widgetViewedCount }
    let mounted = true
    let instance = null
    const setUpAnalytics = (recipeId, experimentName) => {
      const next = new Analytics(recipeId, getHostPageUrl(), experimentName)
      if (!mounted) {
        next.destroy?.()
        return
      }
      instance = next
      next.sendEvent(EventName.WIDGET_VIEWED, eventPayload, partner)
      setAnalytics(next)
    }

    if (children) {
      // If client provide their own UI, we need to handle the click event on container element.
      container?.addEventListener('click', openModal)
      setUpAnalytics(null, null)
    } else if (!tbrecipeId && !externalId) {
      createOrUpdateRecipe(recipeUrl, false, partner, zipCode).then(recipeData => {
        if (!mounted) return
        setRecipe(recipeData)
        setUpAnalytics(recipeData.recipeId, validatedStyle === ButtonStyle.MULTI ? 'multi-button' : null)
      })
    } else {
      // Landing page widget
      setRecipe({ recipeId: tbrecipeId, id: externalId, servings: servingSize, shoppable: true, available: true })
      setUpAnalytics(tbrecipeId, validatedStyle === ButtonStyle.MULTI ? 'multi-button' : null)
    }
    widgetViewedCount += 1

    return () => {
      mounted = false
      container?.removeEventListener('click', openModal)
      instance?.destroy?.()
    }
  }, [])

  // Setup IntersectionObserver to send impression event
  useEffect(() => {
    let observer = null
    if (rootRef?.current && children) {
      observer = new IntersectionObserver(sendImpressionEvent, { threshold: 1 })
      observer.observe(rootRef.current)
    }
    return () => {
      if (observer) observer.disconnect()
    }
  }, [rootRef, children, recipe, analytics])

  const openModal = event => {
    event.preventDefault()
    if (!recipeUrl) return
    setModalVisible(true)
    postBroadcastMessage(buildBroadcastMessage(BroadcastEventName.WIDGET_CLICK))
  }

  const sendImpressionEvent = entries => {
    entries.forEach(entry => {
      if (!isValidIntersectionEntry(entry)) return

      impressionCount += 1
      analytics?.sendEvent(EventName.WIDGET_BUTTON_IMPRESSION, { referrer: getHostPageUrl(), count: impressionCount }, partner)

      if (impressionCount === 1) {
        // Broadcast widget view event only send once
        postBroadcastMessage(buildBroadcastMessage(BroadcastEventName.WIDGET_VIEW))
      }
    })
  }

  if (!analytics && !children) return null
  if (!recipe && !children) return null
  if (!(recipe?.id && recipe?.shoppable && recipe?.available) && !children) return null

  let modelURL = getIframeURL(partner, process.env.GROCERY_PATH)
  if (children) {
    // If client provide their own UI, we need to use the multi button iframe URL
    modelURL = getIframeURL(partner, process.env.MULTI_BUTTON_PATH)
  }

  const widgetPopUpURLParams = new URLSearchParams({
    referrer: getHostPageUrl(),
    disablePoweredByInPopup
  })

  if (recipe) {
    // This id is uuid of external shoppable recipe
    widgetPopUpURLParams.append('id', recipe.id)
    widgetPopUpURLParams.append('servings', recipe.servings)
    widgetPopUpURLParams.append('strictProduct', true)
    widgetPopUpURLParams.append('lang', lang)
    widgetPopUpURLParams.append('isFromMultiButton', validatedStyle === ButtonStyle.MULTI)
    widgetPopUpURLParams.append('substitution', highlightAiAdapter)
  } else if (children) {
    widgetPopUpURLParams.append('url', recipeUrl)
  }

  if (utmSource) widgetPopUpURLParams.append('utm_source', utmSource)
  if (utmCampaign) widgetPopUpURLParams.append('utm_campaign', utmCampaign)
  if (utmMedium) widgetPopUpURLParams.append('utm_medium', utmMedium)
  if (theme) widgetPopUpURLParams.append('theme', theme)
  if (grocers) widgetPopUpURLParams.append('grocers', grocers)
  if (isPreviewMode()) widgetPopUpURLParams.append('preview', '1')
  if (enableAvailabilityAware) widgetPopUpURLParams.append('availabilityAware', true)
  if (shoppablelinkId) widgetPopUpURLParams.append('shoppablelinkId', shoppablelinkId)

  return (
    <AnalyticsContext.Provider value={analytics}>
      <div ref={rootRef}>
        {children || (
          <ShadowDOMContainer>
            <ButtonComponent
              recipeData={recipe}
              onClick={openModal}
              onImpression={sendImpressionEvent}
            />
          </ShadowDOMContainer>
        )}
        <Modal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          iframeSrc={`${modelURL}?${widgetPopUpURLParams.toString()}`}
        />
      </div>
    </AnalyticsContext.Provider>
  )
}

export default WidgetV2
